import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import FormInputField from 'src/components/common/formInputField';
import { useVideosActions } from 'src/store/videos/actions';
import { useDispatch, useSelector } from 'react-redux';
import { SingleVideoModel } from 'src/models/videosModel';
import { RootState } from 'src/store/reducers/state';

interface IProps {
  singleVideo: SingleVideoModel;
  videoId: string;
  setClipboard: (value: boolean) => void;
}
const ShareVideoModal: React.FC<IProps> = ({ singleVideo, videoId, setClipboard }: IProps) => {
  const dispatch = useDispatch();
  const videoAction = useVideosActions(dispatch);
  const [isRedirectedToLogin, setIsRedirectedToLogin] = useState<boolean>();
  const [playPageCopyToClipBoard, setPlayPageCopyToClipBoard] = useState<string>('');
  const [isDisableSeek, setIsDisableSeek] = useState<boolean>(false);
  const [videoPlayerCopyToClipBoard, setVideoPlayerCopyToClipBoard] = useState<string>('');
  const [iframeCopyToClipBoard, setIframeCopyToClipBoard] = useState<string>('');

  const {
    register,
    formState: { errors },
    reset,
    setValue
  } = useForm();

  // Helper to build iframe string
  const buildIframe = (playerUrl: string) =>
    `<iframe width="640" height="360" src="${playerUrl}" frameborder="0" allowfullscreen></iframe>`;

  // Helper to append/remove &disableSeek=true from a URL
  const toggleSeekParam = (url: string, enable: boolean): string => {
    const base = url.includes('&disableSeek=true')
      ? url.split('&disableSeek=true')[0]
      : url;
    return enable ? `${base}&disableSeek=true` : base;
  };

  useEffect(() => {
    if (singleVideo) {
      reset(singleVideo);
      setIsRedirectedToLogin(singleVideo.redirectToLoginUrlEnabled);
      setPlayPageCopyToClipBoard(singleVideo.playUrl);

      const playerUrl = singleVideo.videoPlayerUrl || '';
      setVideoPlayerCopyToClipBoard(playerUrl);
      setIframeCopyToClipBoard(buildIframe(playerUrl));

      if (playerUrl) {
        setValue('iframe', buildIframe(playerUrl));
      }
      setValue('auth', singleVideo.authKey);
    }
  }, [singleVideo]);

  const handleGenerateClick = () => {
    videoAction.generateAuthKeyRequest(videoId);
  };

  const handleRedirectClick = (e: boolean) => {
    const payloadData = { videoId: videoId, redirectToLogin: e };
    videoAction.updateRedirectToLoginRequest(payloadData);
    if (singleVideo) {
      const playUrlSplitted = singleVideo.playUrl.includes('&redirecttologinurl=true')
        ? singleVideo.playUrl.split('&redirecttologinurl=true')[0]
        : singleVideo.playUrl;
      if (e) {
        setIsRedirectedToLogin(true);
        setValue('playUrl', `${playUrlSplitted}&redirecttologinurl=true`);
        setPlayPageCopyToClipBoard(`${playUrlSplitted}&redirecttologinurl=true`);
      } else {
        setIsRedirectedToLogin(false);
        setValue('playUrl', playUrlSplitted);
        setPlayPageCopyToClipBoard(playUrlSplitted);
      }
    }
  };

  const handleDisableSeekClick = (enable: boolean) => {
    setIsDisableSeek(enable);

    const basePlayerUrl = singleVideo?.videoPlayerUrl || '';
    const updatedPlayerUrl = toggleSeekParam(basePlayerUrl, enable);
    const updatedIframe = buildIframe(updatedPlayerUrl);

    // Update Video Player field & clipboard
    setValue('videoPlayerUrl', updatedPlayerUrl);
    setVideoPlayerCopyToClipBoard(updatedPlayerUrl);

    // Update iFrame field & clipboard
    setValue('iframe', updatedIframe);
    setIframeCopyToClipBoard(updatedIframe);
  };

  return (
    <div>
      <div
        className="modal fade"
        id="ShareVideoModal"
        aria-labelledby="ShareVideoModalLabel"
        aria-hidden="true">
        <div className="modal-dialog relative w-auto pointer-events-none max-w-xl">
          <div className="modal-content">
            <div className="relative modal-header flex flex-shrink-0 items-center justify-between p-4 rounded-t-md">
              <h5
                className="text-xl font-semibold leading-normal text-center w-full"
                id="ShareVideoModalLabel">
                Share
              </h5>
              <button
                type="button"
                className="dismiss-btn"
                data-bs-dismiss="modal"
                aria-label="Close"></button>
            </div>
            <div className="modal-body relative py-4 px-10">
              <form>
                <div className="flex items-center">
                  <div className={`form-group mb-3 w-full`}>
                    <FormInputField
                      label={'Play Page'}
                      type={'text'}
                      fieldName={'playUrl'}
                      register={register}
                      errors={errors}
                      isRequired={false}
                    />
                  </div>
                  <i
                    className="color fa fa-copy ml-5 text-lg cursor-pointer"
                    onClick={() => {
                      navigator.clipboard.writeText(playPageCopyToClipBoard);
                      setClipboard(true);
                    }}></i>
                </div>

                <div className="flex items-center">
                  <div className={`form-group mb-3 w-full`}>
                    <FormInputField
                      label={'Video Player'}
                      type={'text'}
                      fieldName={'videoPlayerUrl'}
                      register={register}
                      errors={errors}
                      isRequired={false}
                    />
                  </div>
                  <i
                    className="color fa fa-copy ml-5 text-lg cursor-pointer"
                    onClick={() => {
                      navigator.clipboard.writeText(videoPlayerCopyToClipBoard);
                      setClipboard(true);
                    }}></i>
                </div>
                <div className="flex items-center">
                  <div className={`form-group mb-3 w-full`}>
                    <label className="mb-1 block text-sm">iFrame</label>
                    <textarea className="input-style" {...register('iframe')} rows={4}></textarea>
                  </div>
                  <i
                    className="color fa fa-copy ml-5 text-lg cursor-pointer"
                    onClick={() => {
                      navigator.clipboard.writeText(iframeCopyToClipBoard);
                      setClipboard(true);
                    }}></i>
                </div>
                <div className="flex items-center">
                  <div className={`form-group mb-3 w-full`}>
                    <input type="text" disabled className="input-style" {...register('auth')} />
                    {(singleVideo.authKey === null || '') && (
                      <div className="mt-3" onClick={handleGenerateClick}>
                        <button
                          type="button"
                          className="btn-primary w-auto mt-0 mr-0 ml-auto borderonly-btn">
                          Generate New Auth Key
                        </button>
                      </div>
                    )}
                  </div>
                  <i className="color fa fa-copy ml-5 text-lg invisible"></i>
                </div>

                <div className="flex mt-5 items-center">
                  <div className="form-check form-switch pl-0 relative">
                    <input
                      className="form-check-input switch-btn"
                      type="checkbox"
                      role="switch"
                      checked={isRedirectedToLogin}
                      onChange={(e) => handleRedirectClick(e.target.checked)}
                    />
                    <i className="fas fa-check checkbox-tick hidden"></i>
                    <i className="fas fa-times checkbox-cross"></i>
                  </div>
                  <label
                    className="form-check-label inline-block font-semibold ml-1 text-md"
                    htmlFor="redirect">
                    Redirect to Login Page
                  </label>
                </div>

                {/*Disable Seek Toggle */}
                <div className="flex mt-5 items-center">
                  <div className="form-check form-switch pl-0 relative">
                    <input
                      className="form-check-input switch-btn"
                      type="checkbox"
                      role="switch"
                      checked={isDisableSeek}
                      onChange={(e) => handleDisableSeekClick(e.target.checked)}
                    />
                    <i className="fas fa-check checkbox-tick hidden"></i>
                    <i className="fas fa-times checkbox-cross"></i>
                  </div>
                  <label
                    className="form-check-label inline-block font-semibold ml-1 text-md"
                    htmlFor="disableSeek">
                    Disable Seek on Video Embed
                  </label>
                </div>

              </form>
            </div>

            <div className="modal-footer flex flex-shrink-0 flex-wrap items-center justify-center p-4 pb-6 pt-0 rounded-b-md">
              <button
                type="button"
                className="mx-0 px-6 btn-primary discard-btn w-auto whitespace-nowrap mt-0"
                data-bs-dismiss="modal">
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShareVideoModal;